/**
 * i18n 国际化配置
 * 
 * 支持语言：
 *   - zh-CN: 简体中文（默认）
 *   - en-US: English
 *   - ja-JP: 日本語
 * 
 * 语言持久化：
 *   - 用户选择保存在 localStorage（key: app_language）
 *   - 初始化优先读取用户偏好，其次浏览器语言检测
 * 
 * @author Simon's Cat Travel Team
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import zhCN from './locales/zh-CN.json';
import enUS from './locales/en-US.json';
import jaJP from './locales/ja-JP.json';

/** 持久化 key */
export const LANGUAGE_STORAGE_KEY = 'app_language';

/** 支持的语言列表 */
export const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
  { code: 'en-US', label: 'English', flag: '🇺🇸' },
  { code: 'ja-JP', label: '日本語', flag: '🇯🇵' },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code'];

/** 默认语言 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'zh-CN';

/**
 * i18n 实例初始化
 */
i18n
  // 浏览器语言检测
  .use(LanguageDetector)
  // 绑定 React
  .use(initReactI18next)
  .init({
    resources: {
      'zh-CN': { translation: zhCN },
      'en-US': { translation: enUS },
      'ja-JP': { translation: jaJP },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    
    detection: {
      // 检测优先级：localStorage > navigator
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React 已自动转义
    },
  });

/**
 * 切换语言
 * @param lang 目标语言代码
 */
export function changeLanguage(lang: SupportedLanguage): void {
  i18n.changeLanguage(lang);
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  // 同步更新 HTML lang 属性
  document.documentElement.lang = lang;
}

/**
 * 获取当前语言
 */
export function getCurrentLanguage(): SupportedLanguage {
  return i18n.language as SupportedLanguage;
}

export default i18n;
